import "./HeaderUserIconComponent.scss";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Button } from "@/components/ui/button.tsx";
import UserAvatarComponent from "@/components/common-components/user-avatar/UserAvatarComponent.tsx";
import { ChevronDown, LogOutIcon, UserCheck, Settings } from "lucide-react"; // Added Settings icon
import commonService from "@/services/common.service.ts";
import { userLogout, stopImpersonation } from "@/store/actions/auth.action.ts"; // Added stopImpersonation
import { useDispatch, useSelector } from "react-redux"; // Added useSelector
import { CommonService } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState, useEffect } from "react";
import { useSocket } from "@/services/hooks/useSocket";
import { IAuthReducerState } from "@/store/reducers/auth.reducer";
import { Misc } from "@/constants"; // For typing useSelector
import UserSettingsDialog from "@/screens/app/user-settings/UserSettingsDialog";

const HeaderUserIconComponent = () => {
    // 1. All hooks first
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { disconnect } = useSocket({ persist: true });
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    // Get admin impersonation status from Redux store
    const originalAdminState = useSelector((state: { auth: IAuthReducerState }) => state.auth.originalAdminState);
    const authUser = useSelector((state: { auth: IAuthReducerState }) => state.auth.user);


    const displayUser = authUser || (CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA) ? JSON.parse(CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA)!) : null);


    // 3. Callbacks
    const handleLogout = useCallback(() => {
        setIsLoggingOut(true);
        commonService._account.logoutUser({})
            .then(() => {
                disconnect();
                queryClient.clear();
                dispatch(userLogout());
                commonService.handleMixpanelTrack('log out');
                commonService.handleMixpanelReset();
                // No need to navigate here, relying on App.tsx or route guards to redirect
            })
            .catch((error) => {
                commonService._alert.showToast(error?.response?.data?.message || error?.message || 'Something went wrong', 'error');
            })
            .finally(() => {
                setIsLoggingOut(false);
            });
    }, [dispatch, queryClient, disconnect]);

    const handleStopImpersonating = useCallback(() => {
        dispatch(stopImpersonation());
        alert("Impersonation stopped. You are now back to your admin account.");
        const adminUserListPath =   '/admin/users';
        window.location.href = adminUserListPath;
    }, [dispatch]); // Removed navigate as it's not used due to window.location.href

    const handleOpenSettings = useCallback(() => {
        setIsSettingsOpen(true);
    }, []);

    const handleCloseSettings = useCallback(() => {
        setIsSettingsOpen(false);
    }, []);

    // Listen for communication service trigger to close user settings
    useEffect(() => {
        const subscription = CommonService._communications.closeSettingsSubject.subscribe(() => {
            handleCloseSettings();
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [handleCloseSettings]);


    // 4. Render logic
    return (
        <>
        <Popover>
            <PopoverTrigger asChild>
                <Button variant={"text"} className="text-secondary user-container">
                    <UserAvatarComponent user={displayUser} /> {/* Pass user to avatar */}
                    <ChevronDown strokeWidth={1} size={16} />
                    {originalAdminState && (
                        <span style={{
                            marginLeft: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'orange',
                            backgroundColor: 'rgba(255,165,0,0.1)',
                            padding: '2px 6px',
                            borderRadius: '4px'
                        }}>
                            (Impersonating)
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-58 mt-2 mr-4 user-popover-container">
                <div className={'user-details-wrapper'}>
                    <div className={'user-details-card'}>
                        <div className={'user-details-label-icon'}>
                            <UserAvatarComponent user={displayUser} /> {/* Pass user to avatar */}
                        </div>
                        <div className={'user-details-content-wrapper'}>
                            <div className={'user-name-wrapper'}>
                                {commonService.capitalizeFirstLetter(displayUser?.name)}
                                {originalAdminState && (
                                     <span style={{ display: 'block', fontSize: '0.7rem', color: 'orange' }}>
                                         Impersonating: {displayUser?.email}
                                     </span>
                                 )}
                            </div>
                            {!originalAdminState && ( // Show email only if not impersonating, or adjust as needed
                                <div className={'user-email-value'}>
                                    {displayUser?.email}
                                </div>
                            )}
                        </div>
                    </div>

                    {originalAdminState && (
                        <Button
                            className="stop-impersonation-button px-5 py-4 w-full justify-start"
                            onClick={handleStopImpersonating}
                            variant={"outline"}
                            style={{ marginBottom: '8px' }}
                        >
                           <UserCheck strokeWidth={1} className={'mr-2 h-4 w-4'} /> Stop Impersonating
                        </Button>
                    )}

                    <Button
                        className="logout-button px-5 py-4 w-full justify-start"
                        onClick={handleOpenSettings}
                        variant={"textSecondary"}
                        mixpanelEvent={{
                            name: "user_settings_clicked",
                            properties: {
                                timestamp: new Date().toISOString()
                            }
                        }}
                    >
                       <Settings strokeWidth={1} className={'mr-2 h-4 w-4'} /> Settings
                    </Button>

                    <Button
                        className="logout-button px-5 py-4 w-full justify-start"
                        onClick={handleLogout}
                        loading={isLoggingOut}
                        variant={"textDestructive"}
                        mixpanelEvent={{
                            name: "sign_out_clicked",
                            properties: {
                                timestamp: new Date().toISOString()
                            }
                        }}
                    >
                       <LogOutIcon strokeWidth={1} className={'mr-2 h-4 w-4'} /> Sign Out
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
        
        <UserSettingsDialog 
            isOpen={isSettingsOpen}
            onClose={handleCloseSettings}
        />
        </>
    );
};

export default HeaderUserIconComponent;
